import { NOTICE_STYLES, PAGE_BREAK_MARKUP, rawAttr, type NoticeStyle } from "@wiki/core";
import { lift, setBlockType, wrapIn } from "prosemirror-commands";
import { Fragment, type Node as ProseMirrorNode, type NodeType } from "prosemirror-model";
import { NodeSelection, TextSelection, type Command, type EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { findParentOfType, findTopLevelBlock, nodes } from "../schema";
import { swapWithSibling } from "./util";

export { NOTICE_STYLES };
export type { NoticeStyle };

/** True when every selected text block already has this type and attributes. */
export function blockTypeActive(
  state: EditorState,
  type: NodeType,
  attrs: Record<string, unknown> = {},
): boolean {
  const { from, to } = state.selection;
  let matched = false;
  let mismatched = false;
  state.doc.nodesBetween(from, to, (node) => {
    if (!node.isTextblock) return true;
    const sameAttrs = Object.entries(attrs).every(([key, value]) => node.attrs[key] === value);
    if (node.type === type && sameAttrs) matched = true;
    else mismatched = true;
    return false;
  });
  return matched && !mismatched;
}

export const setParagraph: Command = setBlockType(nodes.paragraph);

/** `^⇧1`–`^⇧4` and the slash-menu heading items; a second press goes back. */
export function toggleHeading(level: number): Command {
  return (state, dispatch, view) => {
    if (blockTypeActive(state, nodes.heading, { level })) {
      return setBlockType(nodes.paragraph)(state, dispatch, view);
    }
    return setBlockType(nodes.heading, { level })(state, dispatch, view);
  };
}

export function toggleCodeBlock(language = ""): Command {
  return (state, dispatch, view) => {
    if (blockTypeActive(state, nodes.codeBlock)) {
      return setBlockType(nodes.paragraph)(state, dispatch, view);
    }
    return setBlockType(nodes.codeBlock, { language })(state, dispatch, view);
  };
}

export const toggleBlockquote: Command = (state, dispatch, view) => {
  if (findParentOfType(state.selection.$from, nodes.blockquote) !== null) {
    return lift(state, dispatch);
  }
  return wrapIn(nodes.blockquote)(state, dispatch, view);
};

/** `---` is a divider, `***` a page break (`markdown-dialect.md` §6.8). */
export function insertRule(markup = "---"): Command {
  return (state, dispatch) => {
    const rule = nodes.hr.createChecked({ markup });
    if (dispatch) dispatch(state.tr.replaceSelectionWith(rule).scrollIntoView());
    return true;
  };
}

export const insertDivider = insertRule("---");
export const insertPageBreak = insertRule(PAGE_BREAK_MARKUP);

export function isPageBreak(node: ProseMirrorNode): boolean {
  return node.type === nodes.hr && rawAttr(node, "markup") === PAGE_BREAK_MARKUP;
}

/** Wrap the selection in a notice, or restyle the notice already around it. */
export function toggleNotice(style: NoticeStyle): Command {
  return (state, dispatch, view) => {
    const found = findParentOfType(state.selection.$from, nodes.notice);
    if (found) {
      if (rawAttr(found.node, "style") === style) return lift(state, dispatch);
      if (dispatch) dispatch(state.tr.setNodeAttribute(found.pos, "style", style));
      return true;
    }
    return wrapIn(nodes.notice, { style })(state, dispatch, view);
  };
}

export function setNoticeStyle(style: NoticeStyle): Command {
  return (state, dispatch) => {
    const found = findParentOfType(state.selection.$from, nodes.notice);
    if (!found) return false;
    if (dispatch) dispatch(state.tr.setNodeAttribute(found.pos, "style", style));
    return true;
  };
}

export const insertMathBlock: Command = (state, dispatch) => {
  const block = nodes.mathBlock.createAndFill();
  if (!block) return false;
  if (dispatch) {
    const tr = state.tr.replaceSelectionWith(block);
    const inside = tr.doc.resolve(
      Math.min(tr.mapping.map(state.selection.from) + 1, tr.doc.content.size),
    );
    dispatch(tr.setSelection(TextSelection.near(inside)).scrollIntoView());
  }
  return true;
};

export function insertInlineMath(content = ""): Command {
  return (state, dispatch) => {
    const node = nodes.mathInline.create(
      null,
      content === "" ? undefined : nodes.text.schema.text(content),
    );
    if (dispatch) dispatch(state.tr.replaceSelectionWith(node, false).scrollIntoView());
    return true;
  };
}

/**
 * A 3 × 3 table (`editor.md` §2). Initial column widths are a third of the
 * editing surface, as the spec requires; with no measurable surface — a
 * headless state, a test — the columns stay unsized and the view distributes
 * them evenly.
 */
export function insertTable(rows = 3, cols = 3, view?: EditorView): Command {
  return (state, dispatch) => {
    const available = view?.dom instanceof HTMLElement ? view.dom.clientWidth : 0;
    const colwidth = available > 0 ? Math.floor(available / cols) : null;
    const cell = (type: NodeType): ProseMirrorNode =>
      type.createChecked({ colwidth }, nodes.paragraph.createAndFill());
    const row = (type: NodeType): ProseMirrorNode =>
      nodes.tableRow.createChecked(
        null,
        Fragment.from(Array.from({ length: cols }, () => cell(type))),
      );
    const body = Array.from({ length: Math.max(1, rows - 1) }, () => row(nodes.tableCell));
    const table = nodes.table.createChecked(null, Fragment.from([row(nodes.tableHeader), ...body]));
    if (dispatch) {
      const tr = state.tr.replaceSelectionWith(table);
      // Map with a backward bias so the position is where the table begins,
      // not where it ends: `+3` then walks into the first cell's paragraph
      // (table, row, cell). Mapping forward puts the caret past the table, and
      // past the end of the document when it was inserted as the last block.
      const start = tr.mapping.map(state.selection.from, -1);
      const inside = tr.doc.resolve(Math.min(start + 3, tr.doc.content.size));
      dispatch(tr.setSelection(TextSelection.near(inside)).scrollIntoView());
    }
    return true;
  };
}

/** `Mod-Alt-↑` / `Mod-Alt-↓`: move the whole top-level block (§10). */
export function moveBlock(direction: -1 | 1): Command {
  return (state, dispatch) => {
    const found = findTopLevelBlock(state.selection.$from);
    if (!found) return false;
    return swapWithSibling(state, found, direction, dispatch);
  };
}

/** Block-handle menu: duplicate the block under the handle. */
export function duplicateBlockAt(pos: number): Command {
  return (state, dispatch) => {
    const node = state.doc.nodeAt(pos);
    if (!node) return false;
    if (dispatch) dispatch(state.tr.insert(pos + node.nodeSize, node).scrollIntoView());
    return true;
  };
}

export function deleteBlockAt(pos: number): Command {
  return (state, dispatch) => {
    const node = state.doc.nodeAt(pos);
    if (!node) return false;
    if (dispatch) dispatch(state.tr.delete(pos, pos + node.nodeSize).scrollIntoView());
    return true;
  };
}

export function selectBlockAt(pos: number): Command {
  return (state, dispatch) => {
    if (!state.doc.nodeAt(pos)) return false;
    if (dispatch) dispatch(state.tr.setSelection(NodeSelection.create(state.doc, pos)));
    return true;
  };
}

/** Insert a `date` mention for today, followed by a space (`editor.md` §2). */
export function insertCurrentDate(now: Date = new Date()): Command {
  return (state, dispatch) => {
    const iso = now.toISOString().slice(0, 10);
    const mention = nodes.mention.create({
      type: "date",
      label: iso,
      modelId: iso,
      id: crypto.randomUUID(),
    });
    if (dispatch) {
      dispatch(state.tr.replaceSelectionWith(mention, false).insertText(" ").scrollIntoView());
    }
    return true;
  };
}
